/**
 * /recepcao, /eventos, /certificados, /integracoes, /diagnosticos, /configuracoes.
 *
 * Fonte normativa: SCRIPT G7, §14 (recepção e eventos), §15 (certificados),
 * §16 (integrações e diagnósticos) e §11 (rotas mínimas).
 *
 * A REGRA QUE ATRAVESSA AS SEIS TELAS
 *
 *   §86: _"Exibir apenas fatos persistidos. Não fabricar documentos. Não
 *   oferecer ações fiscais cujo backend ainda não esteja aprovado."_
 *
 *   Por isso nenhuma delas tem botão de ação. Não há "reenviar", não há
 *   "consultar agora", não há "tentar novamente" — o §92 nomeia esse último e o
 *   proíbe explicitamente para operação fiscal.
 *
 *   O motivo é o G6B inteiro: um botão genérico de retry sobre um documento em
 *   `unknown_state` é uma retransmissão, e retransmissão de documento fiscal
 *   duplica nota. O legado tinha esse botão.
 */

import { useQuery } from '@tanstack/react-query';
import { useSession } from '../lib/session.js';
import {
  fetchArtifacts,
  fetchCertificates,
  fetchDfeCursors,
  fetchDfeReceivedDocuments,
  fetchDfeSyncRuns,
  fetchEvents,
  fetchNfseDocuments,
  fetchProviderInstances,
  type DfeCursorRow,
  type DfeSyncRunRow,
} from '../lib/queries.js';
import { AsyncPanel, ErrorState, LoadingState, ausente, dataBr } from '../components/States.js';

/* ---------------------------------------------------------------------------
   /recepcao — DF-e recebidos
   --------------------------------------------------------------------------- */

/**
 * Três ausências que se parecem, e que a tela não pode confundir.
 *
 * O §22 do G8.2-B pede a distinção entre duas delas; a auditoria da própria
 * tela encontrou uma terceira, e as três produzem a mesma lista vazia:
 *
 *   1. captura não provisionada   não existe cursor para este escopo. Ninguém
 *                                 pediu para capturar nada.
 *   2. captura não executada      o cursor existe e nenhuma consulta foi feita.
 *                                 Nada foi perguntado ao Ambiente Nacional.
 *   3. nenhum documento novo      a consulta foi feita e o Ambiente Nacional
 *                                 respondeu que não há documentos.
 *
 * Só a terceira é informação sobre a EMPRESA. As duas primeiras são informação
 * sobre o SISTEMA, e apresentá-las como "nenhum documento recebido" seria uma
 * afirmação fiscal falsa — o operador concluiria que não chegou nota quando
 * ninguém foi olhar.
 *
 * É a mesma disciplina do `AsyncPanel`, onde perguntar "está vazio?" antes de
 * "falhou?" transforma erro em ausência.
 */
export function RecepcaoPage() {
  const { selected } = useSession();
  const id = selected?.id ?? '';

  const cursores = useQuery({
    queryKey: ['dfe-cursors', id],
    queryFn: () => fetchDfeCursors(id),
    enabled: id !== '',
  });
  const execucoes = useQuery({
    queryKey: ['dfe-sync-runs', id],
    queryFn: () => fetchDfeSyncRuns(id),
    enabled: id !== '',
  });
  const documentos = useQuery({
    queryKey: ['dfe-received', id],
    queryFn: () => fetchDfeReceivedDocuments(id),
    enabled: id !== '',
  });

  const cursor = cursores.data?.[0] ?? null;
  const ultima = execucoes.data?.[0] ?? null;

  /*
   * A ordem das perguntas é a proteção — a mesma do `AsyncPanel`.
   *
   * Carregando e erro vêm primeiro; só depois a tela decide qual das três
   * ausências está diante dela.
   */
  const carregando = cursores.isPending || execucoes.isPending || documentos.isPending;
  const falhou = cursores.isError || execucoes.isError || documentos.isError;

  return (
    <section className="of-page">
      <header className="of-page__header">
        <h1 className="of-page__title">Recepção de DF-e</h1>
        <p className="of-page__subtitle">Documentos fiscais emitidos contra esta empresa.</p>
      </header>

      {carregando ? <LoadingState /> : null}

      {!carregando && falhou ? (
        <ErrorState
          title="Recepção de DF-e"
          error={cursores.error ?? execucoes.error ?? documentos.error}
        />
      ) : null}

      {!carregando && !falhou ? (
        <>
          {/* O estado da captura, sempre — inclusive quando há documentos. */}
          <div className="of-panel-state of-panel-state--empty">
            <strong className="of-panel-state__title">{tituloDaCaptura(cursor, ultima)}</strong>
            <p className="of-panel-state__detail">{detalheDaCaptura(cursor, ultima)}</p>
            {cursor !== null ? (
              <p className="of-panel-state__detail">
                Cursor de NSU em <strong>{cursor.last_nsu}</strong>
                {cursor.max_nsu !== null
                  ? ` · último conhecido no Ambiente Nacional ${cursor.max_nsu}`
                  : ''}
                {cursor.next_allowed_at !== null
                  ? ` · próxima consulta permitida a partir de ${dataBr(cursor.next_allowed_at)}`
                  : ''}
              </p>
            ) : null}
          </div>

          {(documentos.data ?? []).length > 0 ? (
            <table className="of-table">
              <thead>
                <tr>
                  <th>Emitente</th>
                  <th>Chave de acesso</th>
                  <th>Emissão</th>
                  <th>Valor</th>
                  <th>Conteúdo</th>
                  <th>Manifestação</th>
                </tr>
              </thead>
              <tbody>
                {(documentos.data ?? []).map((d) => (
                  <tr key={d.id}>
                    <td>
                      {d.issuer_name}
                      <br />
                      <small>{d.issuer_tax_id}</small>
                    </td>
                    <td>
                      <code>{d.access_key}</code>
                    </td>
                    <td>{dataBr(d.issue_datetime)}</td>
                    <td>{d.total_amount}</td>
                    {/*
                     * "Resumo" e "XML completo" são estados do MESMO documento,
                     * e a distinção não é decorativa: antes da manifestação, o
                     * Ambiente Nacional distribui apenas o resumo. Chamar um de
                     * outro faria a tela prometer um XML que não está no cofre.
                     */}
                    <td>{d.completeness === 'full' ? 'XML completo' : 'Resumo'}</td>
                    <td>{d.manifestation_status === 'none' ? '—' : d.manifestation_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/** O título diz qual das três ausências — ou que há documentos. */
function tituloDaCaptura(cursor: DfeCursorRow | null, ultima: DfeSyncRunRow | null): string {
  if (cursor === null) return 'Captura de DF-e não provisionada';
  if (ultima === null) return 'Captura ainda não executada';
  if (ultima.cstat === CSTAT_SEM_DOCUMENTOS) return 'Nenhum documento novo na última sincronização';
  if (ultima.status === 'blocked') return 'Última sincronização recusada pelo Ambiente Nacional';
  if (ultima.status === 'failed') return 'Última sincronização não concluiu';
  return 'Última sincronização concluída';
}

/** `137` — "Nenhum documento localizado". A resposta que significa "acabou". */
const CSTAT_SEM_DOCUMENTOS = '137';

function detalheDaCaptura(cursor: DfeCursorRow | null, ultima: DfeSyncRunRow | null): string {
  if (cursor === null) {
    return (
      'Não existe cursor de NSU para esta empresa neste ambiente. Provisionar é ato explícito, ' +
      'porque a primeira consulta inicia uma sequência no Ambiente Nacional que ninguém rebobina.'
    );
  }
  if (ultima === null) {
    return (
      'O escopo está provisionado e nenhuma consulta foi feita ainda. A lista abaixo estar vazia ' +
      'não diz nada sobre os documentos da empresa — diz que ninguém perguntou.'
    );
  }

  const quando = dataBr(ultima.started_at);
  if (ultima.cstat === CSTAT_SEM_DOCUMENTOS) {
    return (
      `Em ${quando} o Ambiente Nacional respondeu que não há documentos novos a partir do NSU ` +
      `${ultima.starting_nsu}. Isto é uma afirmação sobre a empresa, e não ausência de captura.`
    );
  }
  if (ultima.status === 'blocked' || ultima.status === 'failed') {
    /*
     * O status vai para a tela EM PORTUGUÊS, e não como o rótulo do enum.
     *
     * `blocked` e `failed` são vocabulário do banco. Quem opera a empresa lê
     * "recusada pelo Ambiente Nacional" e sabe o que aconteceu; lê "blocked" e
     * precisa perguntar a alguém.
     */
    const explicacao =
      ultima.status === 'blocked'
        ? 'o Ambiente Nacional recusou a consulta'
        : 'a sincronização não chegou ao fim';
    return (
      `Em ${quando} ${explicacao}` +
      `${ultima.cstat !== null ? `, com cStat ${ultima.cstat}` : ''}. ` +
      'Enquanto isso não se resolve, a ausência de documentos abaixo não é informação sobre a empresa.'
    );
  }
  return (
    `Em ${quando} a sincronização viu ${ultima.documents_seen} documento(s) e persistiu ` +
    `${ultima.documents_inserted}.`
  );
}

/* ---------------------------------------------------------------------------
   /eventos
   --------------------------------------------------------------------------- */

export function EventosPage() {
  const { selected } = useSession();
  const id = selected?.id ?? '';
  const query = useQuery({
    queryKey: ['eventos', id],
    queryFn: () => fetchEvents(id),
    enabled: id !== '',
  });

  return (
    <section className="of-page">
      <header className="of-page__header">
        <h1 className="of-page__title">Eventos fiscais</h1>
        <p className="of-page__subtitle">
          Cancelamento, carta de correção e demais eventos registrados.
        </p>
      </header>

      <AsyncPanel
        query={query}
        title="Eventos fiscais"
        emptyTitle="Nenhum evento registrado"
        emptyDetail="Nenhum evento fiscal foi persistido para esta identidade."
      >
        {(rows) => (
          <div className="of-table-wrap">
            <table className="of-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Código</th>
                  <th>Seq.</th>
                  <th>Status</th>
                  <th>cStat</th>
                  <th>Motivo</th>
                  <th>Registrado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id}>
                    <td className="of-mono">{e.event_type}</td>
                    <td className="of-mono">{ausente(e.event_code)}</td>
                    <td className="of-mono">{ausente(e.sequence_number)}</td>
                    <td className="of-mono">{e.status}</td>
                    <td className="of-mono">{ausente(e.cstat)}</td>
                    <td>{ausente(e.xmotivo)}</td>
                    <td>{dataBr(e.registered_at ?? e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncPanel>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   /certificados — §15
   --------------------------------------------------------------------------- */

export function CertificadosPage() {
  const { selected } = useSession();
  const id = selected?.id ?? '';
  const query = useQuery({
    queryKey: ['certificados', id],
    queryFn: () => fetchCertificates(id),
    enabled: id !== '',
  });

  const agora = Date.now();
  const trintaDias = 30 * 24 * 60 * 60 * 1000;

  return (
    <section className="of-page">
      <header className="of-page__header">
        <h1 className="of-page__title">Certificados</h1>
        <p className="of-page__subtitle">Metadados dos certificados desta identidade fiscal.</p>
      </header>

      <AsyncPanel
        query={query}
        title="Certificados"
        emptyTitle="Nenhum certificado"
        emptyDetail="Nenhum certificado está vinculado a esta identidade fiscal."
      >
        {(rows) => (
          <div className="of-table-wrap">
            <table className="of-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Titular (CN)</th>
                  <th>CNPJ do titular</th>
                  <th>Emissor</th>
                  <th>Série</th>
                  <th>Válido de</th>
                  <th>Válido até</th>
                  <th>Fingerprint</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const restante = new Date(c.not_after).getTime() - agora;
                  const alerta = c.status === 'active' && restante <= trintaDias;
                  return (
                    <tr key={c.id} className={alerta ? 'of-row--atencao' : undefined}>
                      <td className="of-mono">{c.status}</td>
                      {/* O DN inteiro é técnico e longo; o CN é o que identifica. */}
                      <td>{/CN=([^,]+)/.exec(c.subject_dn)?.[1] ?? c.subject_dn}</td>
                      <td className="of-mono">{ausente(c.subject_tax_id)}</td>
                      <td>{/CN=([^,]+)/.exec(c.issuer_dn)?.[1] ?? c.issuer_dn}</td>
                      <td className="of-mono">{c.serial_number}</td>
                      <td>{dataBr(c.not_before)}</td>
                      <td>
                        {dataBr(c.not_after)}
                        {alerta && <span className="of-cell__hint"> vence em breve</span>}
                      </td>
                      <td className="of-mono of-cell--key">{c.fingerprint_sha256}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AsyncPanel>

      <p className="of-page__footnote">
        Esta tela mostra apenas metadados. O PFX, a senha, a chave privada e o material do keyring
        nunca chegam ao browser — e não é uma escolha da interface: o{' '}
        <code className="of-mono">grant</code> da migration 0037 não concede as colunas de
        armazenamento, então o banco recusaria a consulta.
      </p>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   /integracoes — §16
   --------------------------------------------------------------------------- */

export function IntegracoesPage() {
  const { selected } = useSession();
  const id = selected?.id ?? '';
  const query = useQuery({
    queryKey: ['provedores', id],
    queryFn: () => fetchProviderInstances(id),
    enabled: id !== '',
  });

  return (
    <section className="of-page">
      <header className="of-page__header">
        <h1 className="of-page__title">Integrações</h1>
        <p className="of-page__subtitle">Provedores de NFS-e configurados para esta identidade.</p>
      </header>

      <AsyncPanel
        query={query}
        title="Integrações"
        emptyTitle="Nenhuma integração configurada"
        emptyDetail="Esta identidade fiscal não tem provedor de NFS-e vinculado."
      >
        {(rows) => (
          <div className="of-table-wrap">
            <table className="of-table">
              <thead>
                <tr>
                  <th>Provedor</th>
                  <th>Ambiente</th>
                  <th>Município</th>
                  <th>Credenciamento</th>
                  <th>Status</th>
                  <th>Endpoint</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td className="of-mono">{p.provider_id}</td>
                    <td className="of-mono">{p.environment}</td>
                    <td className="of-mono">{ausente(p.municipality_code)}</td>
                    <td className="of-mono">{ausente(p.credentialing)}</td>
                    <td className="of-mono">{ausente(p.status)}</td>
                    {/* A URL do endpoint é pública e já está no código. O TOKEN
                        não aparece em lugar nenhum — nem aqui, nem no bundle. */}
                    <td className="of-mono of-cell--key">{ausente(p.endpoint_url)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncPanel>

      <p className="of-page__footnote">
        Um provedor listado aqui está cadastrado — não necessariamente operacional. Credencial,
        token e segredo de provedor não passam pelo browser.
      </p>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   /diagnosticos — §16
   --------------------------------------------------------------------------- */

/**
 * Diagnóstico é console, não laboratório (§92).
 *
 * Ele responde "o que o sistema consegue ver daqui" — quais consultas
 * funcionam, quantas linhas voltam, o que a RLS deixa passar. Nenhuma delas
 * toca a rede fiscal, nenhuma emite, nenhuma consulta prefeitura.
 *
 * Um "laboratório" que testasse conectividade com o GIAP a partir do browser
 * precisaria do token no browser, e o §17 fecha essa porta antes de a pergunta
 * ser feita.
 */
export function DiagnosticosPage() {
  const { selected, session, identities } = useSession();
  const id = selected?.id ?? '';

  /*
   * Os hooks são chamados um a um, e não dentro de um `map`.
   *
   * A ordem de chamada dos hooks precisa ser idêntica em toda renderização, e
   * gerá-los a partir de uma lista faria essa garantia depender do conteúdo da
   * lista. Aqui ela depende do código, que é o único lugar onde dá para lê-la.
   */
  const qNfse = useQuery({
    queryKey: ['nfse', id],
    queryFn: () => fetchNfseDocuments(id),
    enabled: id !== '',
  });
  const qArtefatos = useQuery({
    queryKey: ['artefatos', id],
    queryFn: () => fetchArtifacts(id),
    enabled: id !== '',
  });
  const qCertificados = useQuery({
    queryKey: ['certificados', id],
    queryFn: () => fetchCertificates(id),
    enabled: id !== '',
  });
  const qEventos = useQuery({
    queryKey: ['eventos', id],
    queryFn: () => fetchEvents(id),
    enabled: id !== '',
  });
  const qProvedores = useQuery({
    queryKey: ['provedores', id],
    queryFn: () => fetchProviderInstances(id),
    enabled: id !== '',
  });

  const consultas = [
    { nome: 'nfse_documents', q: qNfse },
    { nome: 'fiscal_artifacts', q: qArtefatos },
    { nome: 'fiscal_certificates', q: qCertificados },
    { nome: 'fiscal_document_events', q: qEventos },
    { nome: 'nfse_provider_instances', q: qProvedores },
  ];

  return (
    <section className="of-page">
      <header className="of-page__header">
        <h1 className="of-page__title">Diagnósticos</h1>
        <p className="of-page__subtitle">O que este console alcança, com esta sessão.</p>
      </header>

      <dl className="of-fields">
        <div className="of-field-row">
          <dt className="of-field-row__label">Sessão</dt>
          <dd className="of-field-row__value">
            <span className="of-mono">{session?.user.email ?? '—'}</span>
          </dd>
        </div>
        <div className="of-field-row">
          <dt className="of-field-row__label">Identidades autorizadas</dt>
          <dd className="of-field-row__value">
            <span className="of-mono">{identities.length}</span>
            <span className="of-field-row__note">filtradas pela RLS, não pela interface</span>
          </dd>
        </div>
        <div className="of-field-row">
          <dt className="of-field-row__label">Identidade em foco</dt>
          <dd className="of-field-row__value">
            <span className="of-mono">{selected?.tax_id ?? '—'}</span>
          </dd>
        </div>
      </dl>

      <h2 className="of-section-title">Consultas</h2>
      <div className="of-table-wrap">
        <table className="of-table">
          <thead>
            <tr>
              <th>Relação</th>
              <th>Resultado</th>
              <th>Linhas</th>
              <th>Detalhe</th>
            </tr>
          </thead>
          <tbody>
            {consultas.map(({ nome, q }) => (
              <tr key={nome}>
                <td className="of-mono">{nome}</td>
                <td className="of-mono">
                  {q.isPending ? 'consultando' : q.isError ? 'FALHOU' : 'ok'}
                </td>
                <td className="of-mono">
                  {q.isError || q.isPending ? '—' : (q.data?.length ?? 0)}
                </td>
                <td>
                  {q.isError ? (
                    <code className="of-cell__hint">
                      {q.error instanceof Error ? q.error.message : String(q.error)}
                    </code>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="of-page__footnote">
        Nenhuma linha desta tela toca a rede fiscal. Não há teste de conectividade com prefeitura
        nem com SEFAZ: isso exigiria credencial de provedor no browser, e ela não vai ao browser.
      </p>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   /configuracoes
   --------------------------------------------------------------------------- */

export function ConfiguracoesPage() {
  const { selected, identities, select } = useSession();
  const id = selected?.id ?? '';
  const artefatos = useQuery({
    queryKey: ['artefatos', id],
    queryFn: () => fetchArtifacts(id),
    enabled: id !== '',
  });

  return (
    <section className="of-page">
      <header className="of-page__header">
        <h1 className="of-page__title">Configurações</h1>
        <p className="of-page__subtitle">Identidade fiscal em foco e custódia.</p>
      </header>

      <h2 className="of-section-title">Identidade fiscal</h2>
      <div className="of-table-wrap">
        <table className="of-table">
          <thead>
            <tr>
              <th />
              <th>Razão social</th>
              <th>CNPJ</th>
              <th>Regime</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {identities.map((i) => (
              <tr key={i.id}>
                <td>
                  <input
                    type="radio"
                    name="identidade"
                    checked={i.id === selected?.id}
                    onChange={() => select(i.id)}
                    aria-label={`Selecionar ${i.legal_name}`}
                  />
                </td>
                <td>{i.legal_name}</td>
                <td className="of-mono">{i.tax_id}</td>
                <td className="of-mono">{i.tax_regime}</td>
                <td className="of-mono">{i.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="of-section-title">Cofre fiscal</h2>
      <AsyncPanel query={artefatos} title="Cofre fiscal" emptyTitle="Nenhum artefato custodiado">
        {(rows) => {
          const porTipo = new Map<string, number>();
          for (const a of rows) porTipo.set(a.kind, (porTipo.get(a.kind) ?? 0) + 1);
          return (
            <div className="of-table-wrap">
              <table className="of-table">
                <thead>
                  <tr>
                    <th>Tipo de artefato</th>
                    <th>Quantidade</th>
                  </tr>
                </thead>
                <tbody>
                  {[...porTipo.entries()]
                    .sort((a, b) => b[1] - a[1])
                    .map(([kind, n]) => (
                      <tr key={kind}>
                        <td className="of-mono">{kind}</td>
                        <td className="of-mono">{n}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          );
        }}
      </AsyncPanel>
    </section>
  );
}
